import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, PencilSimple, FilePdf, EnvelopeSimple, Phone, MapPin, Buildings, Calendar, User, Briefcase, GraduationCap, CurrencyEur, CheckCircle, Sparkle } from '@phosphor-icons/react';
import { getContact, getContactMissions } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ClientInvoicesPanel from '../components/ClientInvoicesPanel';
import { COLLEGES, findCollege } from '../constants/colleges';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const isoToFr = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const civiliteLabel = (c) => {
  if (!c) return '';
  const map = { m: 'M.', mr: 'M.', 'm.': 'M.', monsieur: 'M.', mme: 'Mme', mrs: 'Mme', madame: 'Mme', mx: 'Mx' };
  return map[String(c).toLowerCase().trim()] || c;
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-[#5E6D66]">{label}</p>
    <p className="text-sm text-[#1A2421] mt-0.5">{value || <span className="text-[#5E6D66] italic">Non renseigné</span>}</p>
  </div>
);

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missions, setMissions] = useState([]);
  const [loadingMissions, setLoadingMissions] = useState(true);
  const [validateOpen, setValidateOpen] = useState(false);
  const [vRole, setVRole] = useState('commercial');
  const [vCollege, setVCollege] = useState('');
  const [vNote, setVNote] = useState('');
  const [validating, setValidating] = useState(false);

  const isAdmin = ['super_admin', 'admin', 'direction'].includes(currentUser?.role);
  const needsValidation = contact?.auto_created === true || contact?.type_acteur === 'prospect' || (contact?.tags || []).includes('à-compléter');

  const handleValidate = async () => {
    setValidating(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contacts/${id}/validate`,
        { role: vRole || null, college: vCollege || null, type_acteur: 'membre', statut_relation: 'actif', note: vNote || null },
        { withCredentials: true }
      );
      toast.success('Fiche validée avec succès');
      setValidateOpen(false);
      const r = await getContact(id);
      setContact(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    getContact(id)
      .then((r) => setContact(r.data))
      .catch(() => toast.error('Contact introuvable'))
      .finally(() => setLoading(false));
    getContactMissions(id)
      .then((r) => setMissions(r.data.items || []))
      .catch(() => setMissions([]))
      .finally(() => setLoadingMissions(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;
  if (!contact) return <div className="p-6"><Button variant="ghost" onClick={() => navigate('/contacts')}><ArrowLeft size={16} className="mr-2" /> Retour</Button><p className="mt-4">Contact introuvable.</p></div>;

  const college = findCollege(contact.college);
  const fullPhone = [contact.telephone_indicatif, contact.telephone].filter(Boolean).join(' ');
  const fullAddress = [contact.adresse, contact.adresse_quartier, contact.adresse_batiment, contact.adresse_porte, contact.code_postal, contact.ville].filter(Boolean).join(', ');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contact-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')} data-testid="detail-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="contact-detail-name">
              {civiliteLabel(contact.civilite)} {contact.prenom} {contact.nom}
            </h1>
            {contact.fonction && <p className="text-sm text-[#5E6D66]">{contact.fonction}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && needsValidation && (
            <Button
              onClick={() => setValidateOpen(true)}
              className="bg-[#80C51F] hover:bg-[#6BAB16] text-white shadow-md animate-pulse-slow"
              data-testid="detail-validate-btn"
              title="Valider la fiche et convertir le prospect en membre"
            >
              <CheckCircle size={16} weight="fill" className="mr-2" /> Valider la fiche
            </Button>
          )}
          {contact.ged_fiche_id && (
            <Button variant="outline" onClick={() => window.open(`${API}/documents/${contact.ged_fiche_id}/view`, '_blank')} data-testid="detail-view-pdf">
              <FilePdf size={16} className="mr-2 text-[#2D5A4C]" /> Fiche PDF
            </Button>
          )}
          <Button onClick={() => navigate(`/contacts?edit=${contact.id}`)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="detail-edit-btn">
            <PencilSimple size={16} className="mr-2" /> Modifier
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5 space-y-3">
            <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2"><User size={16} /> Identité</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Civilité" value={civiliteLabel(contact.civilite)} />
              <Field label="Type d'acteur" value={contact.type_acteur} />
              <Field label="Statut relation" value={contact.statut_relation} />
              <Field label="Date d'entrée" value={isoToFr(contact.date_entree)} />
              <Field label="Date de naissance" value={isoToFr(contact.date_naissance)} />
              <Field label="Territoire" value={contact.territoire} />
            </div>
            {college && (
              <div className="pt-2">
                <p className="text-xs uppercase tracking-wide text-[#5E6D66]">Collège coopératif</p>
                <Badge variant="outline" className="mt-1 gap-1.5" style={{ borderColor: college.color, color: college.color }} data-testid="detail-college-badge">
                  <college.Icon size={13} />
                  <span className="font-semibold">{college.value}</span>
                  <span>— {college.label}</span>
                  <span className="text-xs">({college.ponderation}%)</span>
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5 space-y-3">
            <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2"><EnvelopeSimple size={16} /> Coordonnées</h2>
            <div className="grid grid-cols-1 gap-3">
              <Field label="Email" value={contact.email && <a href={`mailto:${contact.email}`} className="text-[#2D5A4C] underline">{contact.email}</a>} />
              <Field label="Téléphone" value={fullPhone} />
              <Field label="Adresse" value={fullAddress} />
            </div>
          </CardContent>
        </Card>

        {(contact.raison_sociale || contact.immatriculation_numero || contact.code_naf) && (
          <Card className="border-[#E8E6E1] md:col-span-2">
            <CardContent className="p-5 space-y-3">
              <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2"><Buildings size={16} /> Personne morale rattachée</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Raison sociale" value={contact.raison_sociale} />
                <Field label="Statut juridique" value={contact.statut_juridique} />
                <Field label="Immatriculation" value={contact.immatriculation_numero} />
                <Field label="Code NAF" value={contact.code_naf} />
                <Field label="Libellé NAF" value={contact.naf_label} />
                {contact.secteurs && contact.secteurs.length > 0 && (
                  <div className="col-span-2 md:col-span-3">
                    <p className="text-xs uppercase tracking-wide text-[#5E6D66]">Secteurs</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {contact.secteurs.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Missions coopératives du contact */}
      <Card className="border-[#E8E6E1]" data-testid="contact-missions-section">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Briefcase size={16} className="text-[#2D5A4C]" /> Missions coopératives
            </h2>
            <Badge variant="outline" className="text-xs">{missions.length}</Badge>
          </div>
          {loadingMissions ? (
            <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#2D5A4C]" /></div>
          ) : missions.length === 0 ? (
            <p className="text-sm text-[#5E6D66] py-4 text-center">Ce contact ne participe à aucune mission coopérative pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {missions.map((m) => {
                const isCipa = m.type_dossier === 'mission_coop_cipaesse';
                return (
                  <div
                    key={m.id}
                    className="p-3 bg-[#F9F8F5] hover:bg-[#F0EFEB] rounded border border-[#E8E6E1] cursor-pointer transition-colors"
                    onClick={() => navigate(`/dossiers?open=${m.id}`)}
                    data-testid={`contact-mission-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${isCipa ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                            {isCipa ? 'CIPAESSE (O\u2019SCOP)' : 'CIPASCOP'}
                          </Badge>
                          <span className="text-[10px] font-mono text-[#5E6D66]">{m.numero_dossier}</span>
                          <Badge className={`text-[10px] ${m.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' : m.statut === 'valide' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>{m.statut}</Badge>
                        </div>
                        <p className="text-sm font-semibold text-[#1A2421] truncate">{m.mission_intitule || m.objet_besoin || 'Mission sans titre'}</p>
                        {m.mission_descriptif && <p className="text-xs text-[#5E6D66] mt-0.5 line-clamp-2">{m.mission_descriptif}</p>}
                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#5E6D66] flex-wrap">
                          {m.mission_lieu && <span className="flex items-center gap-1"><MapPin size={10} /> {m.mission_lieu}</span>}
                          {m.mission_date_debut && <span className="flex items-center gap-1"><Calendar size={10} /> {isoToFr(m.mission_date_debut)}{m.mission_date_fin ? ` → ${isoToFr(m.mission_date_fin)}` : ''}</span>}
                          {m.mission_qualification && <span className="flex items-center gap-1"><GraduationCap size={10} /> {m.mission_qualification}</span>}
                        </div>
                      </div>
                      {m.mission_paiement_previsionnel != null && (
                        <div className="text-right flex-shrink-0">
                          <p className="text-[10px] text-[#5E6D66]">Prévisionnel</p>
                          <p className="text-sm font-bold text-[#2D5A4C]">{Number(m.mission_paiement_previsionnel).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Factures du contact */}
      <ClientInvoicesPanel contactId={contact.id} />

      {contact.ged_fiche_id && (
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2 mb-3"><FilePdf size={16} /> Fiche PDF GED (v{contact.fiche_version || 1})</h2>
            <iframe className="w-full h-[600px] border border-[#E8E6E1] rounded-md" src={`${API}/documents/${contact.ged_fiche_id}/view`} title="Fiche PDF" data-testid="detail-pdf-iframe" />
          </CardContent>
        </Card>
      )}

      <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
        <DialogContent data-testid="validate-dialog" className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#80C51F] flex items-center justify-center text-white">
                <Sparkle size={20} weight="fill" />
              </div>
              <div>
                <DialogTitle>Valider la fiche</DialogTitle>
                <DialogDescription>Convertir {contact.prenom} {contact.nom} en membre et lui attribuer un rôle.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label>Rôle applicatif</Label>
              <Select value={vRole} onValueChange={setVRole}>
                <SelectTrigger data-testid="validate-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="membre">Membre</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="instructeur">Instructeur</SelectItem>
                  <SelectItem value="administratif">Administratif</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="juridique">Juridique</SelectItem>
                  <SelectItem value="direction">Direction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Collège <span className="text-[10px] text-[#5E6D66] font-normal">(optionnel)</span></Label>
              <Select value={vCollege} onValueChange={setVCollege}>
                <SelectTrigger data-testid="validate-college"><SelectValue placeholder="Sélectionner un collège" /></SelectTrigger>
                <SelectContent>
                  {COLLEGES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Note interne <span className="text-[10px] text-[#5E6D66] font-normal">(optionnel)</span></Label>
              <Textarea rows={3} value={vNote} onChange={(e) => setVNote(e.target.value)} placeholder="Contexte, décision de validation…" data-testid="validate-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateOpen(false)} disabled={validating}>Annuler</Button>
            <Button onClick={handleValidate} disabled={validating} className="bg-[#80C51F] hover:bg-[#6BAB16] text-white" data-testid="validate-submit">
              {validating ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle size={14} weight="fill" className="mr-1.5" /> Valider et attribuer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
